import * as types from '../types';
import { IPortalApi } from '../contracts';
import { HttpApi } from './HttpApi';

const BASE = '/portal';
const enc = encodeURIComponent;

/** Only the values that are set go on the query string (the server rejects blanks). */
function setParams(query: object): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(query as Record<string, string | number | undefined>).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params[key] = value;
  });
  return params;
}

/** Only the filters that are set go on the query string (the server rejects blanks). */
function listParams(query: types.PortalListQuery = {}): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.propertyId) params.propertyId = query.propertyId;
  if (query.page) params.page = query.page;
  if (query.limit) params.limit = query.limit;
  return params;
}

export class PortalHttpApi extends HttpApi implements IPortalApi {
  async registerPortalAccount(data: types.PortalRegisterRequest): Promise<types.PortalRegisterResult> {
    return (await this.http.post<types.APIResponse<types.PortalRegisterResult>>(`${BASE}/register`, data)).data.data;
  }

  async verifyPortalEmail(data: types.PortalTokenPasswordRequest): Promise<types.PortalVerifyResult> {
    return (await this.http.post<types.APIResponse<types.PortalVerifyResult>>(`${BASE}/verify-email`, data)).data.data;
  }

  async acceptPortalInvite(data: types.PortalTokenPasswordRequest): Promise<types.PortalOkResult> {
    return (await this.http.post<types.APIResponse<types.PortalOkResult>>(`${BASE}/invite/accept`, data)).data.data;
  }

  async requestPortalPasswordReset(data: { tenantSlug: string; email: string }): Promise<types.PortalResetRequestResult> {
    return (await this.http.post<types.APIResponse<types.PortalResetRequestResult>>(`${BASE}/password-reset/request`, data)).data.data;
  }

  async confirmPortalPasswordReset(data: types.PortalTokenPasswordRequest): Promise<types.PortalOkResult> {
    return (await this.http.post<types.APIResponse<types.PortalOkResult>>(`${BASE}/password-reset/confirm`, data)).data.data;
  }

  async getPortalMe(): Promise<types.PortalMe> {
    return (await this.http.get<types.APIResponse<types.PortalMe>>(`${BASE}/me`)).data.data;
  }

  async listPortalInvoices(query: types.PortalListQuery = {}): Promise<types.PortalPage<types.PortalInvoiceSummary>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.PortalInvoiceSummary>>>(`${BASE}/invoices`, { params: listParams(query) })).data.data;
  }

  async getPortalInvoice(id: string): Promise<types.PortalInvoiceDetail> {
    return (await this.http.get<types.APIResponse<types.PortalInvoiceDetail>>(`${BASE}/invoices/${enc(id)}`)).data.data;
  }

  async listPortalJobs(query: types.PortalListQuery = {}): Promise<types.PortalPage<types.PortalJob>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.PortalJob>>>(`${BASE}/jobs`, { params: listParams(query) })).data.data;
  }

  async getPortalJob(id: string): Promise<types.PortalJob> {
    return (await this.http.get<types.APIResponse<types.PortalJob>>(`${BASE}/jobs/${enc(id)}`)).data.data;
  }

  async getPortalStatement(query: { page?: number; limit?: number } = {}): Promise<types.PortalStatement> {
    const params = listParams(query);
    return (await this.http.get<types.APIResponse<types.PortalStatement>>(`${BASE}/statement`, { params })).data.data;
  }

  async getPortalProfile(): Promise<types.PortalProfile> {
    return (await this.http.get<types.APIResponse<types.PortalProfile>>(`${BASE}/profile`)).data.data;
  }

  async updatePortalProfile(data: types.PortalProfileUpdate): Promise<types.PortalProfile> {
    return (await this.http.patch<types.APIResponse<types.PortalProfile>>(`${BASE}/profile`, data)).data.data;
  }

  async getPortalPayConfig(invoiceId: string): Promise<types.PublicPayConfig> {
    return (await this.http.get<types.APIResponse<types.PublicPayConfig>>(`${BASE}/invoices/${enc(invoiceId)}/pay-config`)).data.data;
  }

  async payPortalInvoice(invoiceId: string, data: types.PortalPayRequest): Promise<types.PublicPayResult> {
    return (await this.http.post<types.APIResponse<types.PublicPayResult>>(`${BASE}/invoices/${enc(invoiceId)}/pay`, data)).data.data;
  }

  async getPortalPaymentMethods(): Promise<types.PortalCards> {
    return (await this.http.get<types.APIResponse<types.PortalCards>>(`${BASE}/payment-methods`)).data.data;
  }

  async addPortalPaymentMethod(data: types.PortalAddCardRequest): Promise<types.PortalPaymentMethod> {
    return (await this.http.post<types.APIResponse<types.PortalPaymentMethod>>(`${BASE}/payment-methods`, data)).data.data;
  }

  async removePortalPaymentMethod(methodId: string): Promise<types.PortalPaymentMethod> {
    return (await this.http.delete<types.APIResponse<types.PortalPaymentMethod>>(`${BASE}/payment-methods/${enc(methodId)}`)).data.data;
  }

  async setPortalAutopay(methodId: string, data: types.PortalAutopayRequest): Promise<types.PortalPaymentMethod> {
    return (await this.http.put<types.APIResponse<types.PortalPaymentMethod>>(`${BASE}/payment-methods/${enc(methodId)}/autopay`, data)).data.data;
  }

  async listPortalRequestableItems(): Promise<types.PortalRequestableItem[]> {
    return (await this.http.get<types.APIResponse<types.PortalRequestableItem[]>>(`${BASE}/requestable-items`)).data.data;
  }

  async createPortalServiceRequest(data: types.PortalServiceRequestInput): Promise<types.PortalServiceRequest> {
    return (await this.http.post<types.APIResponse<types.PortalServiceRequest>>(`${BASE}/service-requests`, data)).data.data;
  }

  async listPortalServiceRequests(query: types.RequestPageQuery = {}): Promise<types.PortalPage<types.PortalServiceRequest>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.PortalServiceRequest>>>(`${BASE}/service-requests`, { params: listParams(query) })).data.data;
  }

  async getPortalServiceRequest(id: string): Promise<types.PortalServiceRequest> {
    return (await this.http.get<types.APIResponse<types.PortalServiceRequest>>(`${BASE}/service-requests/${enc(id)}`)).data.data;
  }

  async createPortalRescheduleRequest(jobId: string, data: types.PortalRescheduleInput): Promise<types.PortalRescheduleRequest> {
    return (await this.http.post<types.APIResponse<types.PortalRescheduleRequest>>(`${BASE}/jobs/${enc(jobId)}/reschedule-requests`, data)).data.data;
  }

  async listPortalRescheduleRequests(jobId: string): Promise<types.PortalRescheduleRequest[]> {
    return (await this.http.get<types.APIResponse<types.PortalRescheduleRequest[]>>(`${BASE}/jobs/${enc(jobId)}/reschedule-requests`)).data.data;
  }

  async cancelPortalRescheduleRequest(jobId: string, requestId: string): Promise<types.PortalRescheduleRequest> {
    return (await this.http.delete<types.APIResponse<types.PortalRescheduleRequest>>(`${BASE}/jobs/${enc(jobId)}/reschedule-requests/${enc(requestId)}`)).data.data;
  }

  async listPortalRequestableBundles(query: { propertyId?: string; startDate?: string } = {}): Promise<types.PortalRequestableBundle[]> {
    return (await this.http.get<types.APIResponse<types.PortalRequestableBundle[]>>(`${BASE}/requestable-bundles`, { params: setParams(query) })).data.data;
  }

  async createPortalBundleRequest(data: types.PortalBundleRequestInput): Promise<types.PortalEnrollment> {
    return (await this.http.post<types.APIResponse<types.PortalEnrollment>>(`${BASE}/bundle-requests`, data)).data.data;
  }

  async listPortalEnrollments(query: types.PortalEnrollmentListQuery = {}): Promise<types.PortalEnrollmentPage> {
    return (await this.http.get<types.PortalEnrollmentPage>(`${BASE}/enrollments`, { params: setParams(query) })).data;
  }

  async getPortalEnrollment(id: string): Promise<types.PortalEnrollment> {
    return (await this.http.get<types.APIResponse<types.PortalEnrollment>>(`${BASE}/enrollments/${enc(id)}`)).data.data;
  }

  async setPortalEnrollmentAutoRenew(id: string, autoRenew: boolean): Promise<types.PortalEnrollment> {
    return (await this.http.patch<types.APIResponse<types.PortalEnrollment>>(`${BASE}/enrollments/${enc(id)}`, { autoRenew })).data.data;
  }
}
